// TODO: Drop the fragment half if we switch to canonical CDN URLs / iframe `src` and
// stop injecting `<base>`. The breakout half stays as long as we render in a sandbox.

/** Marks messages posted by the injected script, so the viewer can tell them apart. */
export const GENERATED_HTML_MESSAGE_SOURCE = 'evergpt-generated-html'

/** "The visitor clicked a link belonging to this app — navigate on my behalf." */
export const GENERATED_HTML_NAVIGATE_MESSAGE = 'navigate'

export interface GeneratedHtmlNavigateMessage {
  source: typeof GENERATED_HTML_MESSAGE_SOURCE
  type: typeof GENERATED_HTML_NAVIGATE_MESSAGE
  /** Absolute URL, always same-origin with the document's `<base>`. */
  href: string
}

export interface PublishedHtmlLinkScriptOptions {
  /**
   * Hand same-origin links to the parent instead of opening a tab, letting the viewer
   * route them in place. Requires a `<base>` (that's how the frame learns the app's
   * origin) and a listener on the parent — see `useGeneratedHtmlNavigation`.
   *
   * On for the published viewers, whose iframe fills the tab, so following a link there
   * should feel like following a link on any page. Off for the chat preview, where the
   * iframe is a panel inside the app and replacing the app around it would be a far more
   * disruptive thing than opening a tab.
   */
  delegateSameOriginLinks?: boolean
}

/**
 * Click behaviour injected into every iframe that renders generated HTML — the chat
 * preview and both published viewers. Three jobs:
 *
 * **1. Fragment links stay in the document.** When a `srcdoc` document carries
 * `<base href="https://…/dir/">`, a fragment-only link (`href="#id"`) resolves against
 * that URL and triggers a navigation instead of a scroll.
 *
 * **2. Same-origin links go to the parent** (when `delegateSameOriginLinks` is set). The
 * viewer turns them into a client-side route change, so a platform link like `/library`
 * lands in the same tab with the app already booted and authenticated. The frame can only
 * ask; the parent decides, which is why no `allow-top-navigation` token is needed — one
 * would let any click anywhere in generated markup redirect the visitor's tab.
 *
 * **3. Everything else leaves the frame.** The frame is sandboxed without
 * `allow-same-origin` (see `GENERATED_HTML_SANDBOX`), so navigating it in place loads the
 * destination at origin `null`. Forcing `target="_blank"` sends it to a real tab that
 * `allow-popups-to-escape-sandbox` lets load at its true origin.
 *
 * Left alone, because a forced target breaks them: non-HTTP schemes (`mailto:`, `tel:`,
 * `javascript:`) and anchors carrying `download` (covered by `allow-downloads`).
 *
 * The listener runs in the capture phase, before the page's own handlers. A page that
 * calls `preventDefault()` still wins — the retargeted anchor simply never navigates.
 */
export function publishedHtmlLinkScriptSource(
  options: PublishedHtmlLinkScriptOptions = {},
): string {
  const delegate = options.delegateSameOriginLinks === true
  return `(function(){
var DELEGATE=${delegate ? 'true' : 'false'}
var MESSAGE_SOURCE=${JSON.stringify(GENERATED_HTML_MESSAGE_SOURCE)}
var MESSAGE_TYPE=${JSON.stringify(GENERATED_HTML_NAVIGATE_MESSAGE)}
var SCHEME=/^([a-zA-Z][a-zA-Z0-9+.-]*):/
function isHttpish(href){
var match=SCHEME.exec(href)
if(!match)return true
var scheme=match[1].toLowerCase()
return scheme==="http"||scheme==="https"
}
function sameOriginTarget(href){
try{
var base=new URL(document.baseURI)
if(base.protocol!=="http:"&&base.protocol!=="https:")return null
var url=new URL(href,base)
return url.origin===base.origin?url:null
}catch(error){return null}
}
function breakOut(a){
a.setAttribute("target","_blank")
var rel=a.getAttribute("rel")||""
if(rel.indexOf("noopener")<0)rel+=" noopener"
if(rel.indexOf("noreferrer")<0)rel+=" noreferrer"
a.setAttribute("rel",rel.replace(/^\\s+/,""))
}
document.addEventListener("click",function(e){
var a=e.target&&e.target.closest&&e.target.closest("a[href]")
if(!a)return
var href=a.getAttribute("href")
if(href==null||href==="")return
if(href.charAt(0)==="#"){
if(href==="#"){e.preventDefault();window.scrollTo({top:0,behavior:"smooth"});return}
var id=decodeURIComponent(href.slice(1))
if(!id)return
var el=document.getElementById(id)||document.getElementsByName(id)[0]
if(!el)return
e.preventDefault()
el.scrollIntoView({behavior:"smooth",block:"start"})
return
}
if(a.hasAttribute("download"))return
if(DELEGATE){
var target=sameOriginTarget(href)
if(target){
e.preventDefault()
try{
parent.postMessage({source:MESSAGE_SOURCE,type:MESSAGE_TYPE,href:target.href},target.origin)
}catch(error){
// Parent unreachable: the click is already cancelled, so open it ourselves.
window.open(target.href,"_blank","noopener")
}
return
}
}
if(isHttpish(href))breakOut(a)
},true)
})()`
}

/** Serialized `<script>` for string-based head injection (split closing tag for HTML parsers). */
export function publishedHtmlLinkScriptHtml(
  options: PublishedHtmlLinkScriptOptions = {},
): string {
  return '<script>' + publishedHtmlLinkScriptSource(options) + '</scr' + 'ipt>'
}
